/**
 * membersView.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import React from "react";

import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";

import { AuthContext } from "../auth/auth.context";
import { MeetingContext } from "./meeting.context";

import { SubscriberStream } from "../models/stream";

import { conferenceServiceFactory } from "../services/conferencing.service";
import FormControlLabel from "@mui/material/FormControlLabel";

interface Props {
  streams: Array<SubscriberStream>
  setMuteNewStreams: (shouldMute: boolean) => void
}
export function MembersViewComponent(props: Props): React.ReactElement<any, any> {
  const meetingContext = React.useContext<MeetingContext>(MeetingContext);
  const authContext = React.useContext<AuthContext>(AuthContext);

  const isHost = meetingContext.meeting !== null &&
    authContext.membership !== null &&
    meetingContext.meeting.hostUserIDs.includes(authContext.membership.userID)

  async function decideUser(index: number, allow: boolean) {
    if (!meetingContext.waitingDevices) { return; }
    const waitingDevice = meetingContext.waitingDevices[index];
    try {
      console.log("allowing user:", waitingDevice.userID || waitingDevice.phoneNumber, allow);
      if (waitingDevice.phoneNumber) {
        await conferenceServiceFactory().decideWaitingCaller(
          waitingDevice.accountID, waitingDevice.meetingID, waitingDevice._id, allow);
      } else if (waitingDevice.userID) {
        // this is always true if the phoneNumber doesn't exist, just making TS happy
        await conferenceServiceFactory().decideWaitingUser(
          waitingDevice.accountID, waitingDevice.meetingID, waitingDevice.userID, allow);
      } else {
        console.error("waiting device is missing both userID and phoneNumber");
        return;
      }

      const arr = Array.from(meetingContext.waitingDevices);
      arr.splice(index, 1);
      meetingContext.setWaitingDevices(arr);
    } catch (err) {
      console.error("Error admitting user:", err);
    }
  }
  const onMuteNewStreamsChange = () => {
    props.setMuteNewStreams(!meetingContext.muteNewStreams);
  }

  const hasWaiting = meetingContext && meetingContext.waitingDevices &&
    meetingContext.waitingDevices.length > 0;

  return <>
    <header className="group-header-nav">
      <IconButton className="toggle-right-btn" size="small" disableRipple disableTouchRipple
        onClick={meetingContext.toggleRightDetail}>
        <CloseIcon />
      </IconButton>
      <h3>Manage Meeting</h3>
    </header>
    <div className={"right-side-container meeting-member-list-container" +
      (isHost ? " isHost" : "") +
      (hasWaiting ? " withWaiting" : "")}>
      <section className="waiting-members-section">
        <h3>Waiting Room</h3>
        {meetingContext.waitingDevices && meetingContext.waitingDevices.map((dev, i) =>
          <div key={dev._id} className="waiting-member-item">
            <span>{dev.firstname} {dev.lastname}</span>
            <div className="right-controls">
              <IconButton size="small" onClick={() => decideUser(i, true)}>
                <Tooltip title="Accept">
                  <i className="fa-solid fa-circle-check" />
                </Tooltip>
              </IconButton>
              <IconButton size="small" onClick={() => decideUser(i, false)}>
                <Tooltip title="Reject">
                  <i className="fa-solid fa-circle-xmark" />
                </Tooltip>
              </IconButton>
            </div>
          </div>)}
      </section>
      <h3>Participants</h3>
      <div className="host-controls">
        <Button size="small" variant="contained" color="secondary" className="force-mute-all-btn" 
          onClick={() => meetingContext.hostControlsProvider?.forceMuteAllButMe()}>
          Mute All
        </Button>
        <FormControlLabel
          control={<Checkbox
            checked={meetingContext.muteNewStreams}
            onChange={onMuteNewStreamsChange}
          />}
          label="Mute New Participants"
        />
      </div>
      {props.streams.map(stream =>
        <div key={stream.streamId} className="meeting-member-item">
          <span className="stream-name">{stream.name}</span>
          <div className="right-controls">
            {stream.videoType === "screen" &&
              <IconButton size="small" onClick={() => meetingContext.hostControlsProvider?.forceUnpublish(stream)}>
                <Tooltip title="Force Unpublish">
                  <i className="fa-solid fa-video-slash" />
                </Tooltip>
              </IconButton>}
            <IconButton size="small" onClick={() => meetingContext.hostControlsProvider?.bootStream(stream)}>
              <Tooltip title="Boot Participant">
                <i className="fa-solid fa-person-falling-burst" />
              </Tooltip>
            </IconButton>
            <IconButton size="small" onClick={() => meetingContext.hostControlsProvider?.forceMuteAudio(stream)}>
              <Tooltip title="Force Mute">
                <i className="fa-solid fa-microphone-slash" />
              </Tooltip>
            </IconButton>
          </div>
        </div>
      )}
    </div>
  </>;
}
